import { Select, Table, TableColumnsType } from "antd";
import moment from "moment";
import QueryString from "qs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import { getCurrentSemester } from "../../api/semesters.api";
import { getPortfolios, getTutorClasses2, getPortfolioSubjectList } from "../../api/tutor.api";
import Button from "../../components/Button";
import ShowPortfolioModal from "../../components/class/portfolio/modals/ShowPortfolioModal";
import ErrorResponse from "../../components/ErrorResponse";
import SimpleHeader from "../../components/layout/SimpleHeader";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";
import SelectSemester from "../../components/SelectSemester";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { getCommonCodeLabel } from "../../lib/store";
import { IGetCurrentSemester } from "../../models/semesters.model";
import { IGetPortfolios, IGetTutorClasses2, IPortfolioItem, IGetPortfolioSubject } from "../../models/tutor.model";
import { RootState } from "../../store/rootReducer";
import SelectEduStep from "../../components/SelectEduStep";
import ConfirmModal from '../../components/modals/ConfirmModal';
import { multiApprovalProfolio } from "../../api/tutor.api";
import { IResponse } from "../../models/common.api.model";

const ClassPortfolio = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // GR0004 = 포트폴리오 강사승인 상태
  const GR0004 = commonCodes.find((item) => item.commonCode === 'GR0004')?.detailCode;
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetPortfolios>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetTutorClasses2>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetCurrentSemester>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IResponse<String>>();
  const { data: res5, isLoading: isLoading5, error: error5, execute: execute5 } = useAxios<IGetPortfolioSubject>();

  const [page, setPage] = useState(1);
  const [param, setParam] = useState();
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [learningTypeCode, setLearningTypeCode] = useState<string>();
  const [classCode, setClassCode] = useState<string>();
  const [approvalStatus, setApprovalStatus] = useState('WT');
  const [memberName, setMemberName] = useState('');
  const [showPortfolioVisible, setShowPortfolioVisible] = useState(false);
  const [item, setItem] = useState<IPortfolioItem>();
  const [selectsKeys, setSelectsKeys] = useState<React.Key[]>([]);
  const [confirmApprovalVisible, setConfirmApprovalVisible] = useState(false);
  const [subjectCode, setSubjectCode] = useState<string>();

  useEffect(() => {
    if (query.semesterCode) {
      initSelects();
      doGetPortfolios();
    }
  }, [query.page, query.semesterCode, query.eduStepCode, query.learningTypeCode, query.classCode, query.approvalStatus, query.memberName, query.subjectCode]);

  useEffect(() => {
    execute2(getTutorClasses2());
    execute3(getCurrentSemester());
    execute5(getPortfolioSubjectList());
  }, []);

  useEffect(() => {
    if (res3 && !isLoading3) {
      setSemesterCode(res3.data?.semesterCode);
      doGetPortfolios(res3.data?.semesterCode)
    }
  }, [res3, isLoading3]);

  // 승인대기 목록 체크박스 디폴트 체크
  useEffect(() => {
    if (res && !isLoading && res.result === 'success') {
      const initialSelectedKeys = res.data
        .filter((item: IPortfolioItem) => item.approvalStatus !== 'AP' && item.approvalStatus !== 'RJ')
        .map((item: IPortfolioItem) => item.resultCode);
      setSelectsKeys(initialSelectedKeys);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if(res4 && !isLoading4) {
      if(res4.result === 'success') {
        successNotification(t('common.message.save1')); 
        setConfirmApprovalVisible(false);
        initSelects();
        doGetPortfolios();
      }
    }
  }, [res4, isLoading4]);

  const initSelects = () => {
    setSelectsKeys([]);
  }

  const doGetPortfolios = (_semesterCode?: number) => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
      approvalStatus,
      semesterCode: _semesterCode || semesterCode,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.semesterCode) {
      _param.semesterCode = Number(query.semesterCode);
      setSemesterCode(_param.semesterCode);
    }
    if (query.eduStepCode) {
      _param.eduStepCode = String(query.eduStepCode);
      setEduStepCode(_param.eduStepCode);
    }
    if (query.learningTypeCode) {
      _param.learningTypeCode = String(query.learningTypeCode);
      setLearningTypeCode(_param.learningTypeCode);
    }
    if (query.classCode) {
      _param.classCode = String(query.classCode);
      setClassCode(_param.classCode);
    }
    if (query.approvalStatus) {
      _param.approvalStatus = String(query.approvalStatus);
      setApprovalStatus(_param.approvalStatus);
    }
    if (query.memberName) {
      _param.memberName = String(query.memberName);
      setMemberName(_param.memberName);
    }
    if (query.subjectCode) {
      _param.subjectCode = Number(query.subjectCode);
      setSubjectCode(_param.subjectCode);
    }

    execute(getPortfolios(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }

  const onSearch = () => {
    if (!semesterCode) {
      return infoNotification(t('class.class.message4'));
    }

    const param: any = { page: 1, semesterCode };

    if (eduStepCode) param.eduStepCode = eduStepCode;
    if (learningTypeCode) param.learningTypeCode = learningTypeCode;
    if (classCode) param.classCode = classCode;
    if (approvalStatus) param.approvalStatus = approvalStatus;
    if (memberName) param.memberName = memberName;
    if (subjectCode) param.subjectCode = subjectCode;

    history.push(`/class/portfolio?${QueryString.stringify(param)}`);
  }

  const rowSelection = {
    selectedRowKeys: selectsKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: any) => {
      setSelectsKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: any) => ({
      disabled: (record.approvalStatus === 'AP' || record.approvalStatus === 'RJ'),
    })
  };

  const onMultiApprovalCheck = () => {
    if(selectsKeys.length === 0) {
      return infoNotification(t('class.portfolio.message3'));
    } else {
      setConfirmApprovalVisible(true);
    }
  }

  const onMultiApproval = () => {
    if(selectsKeys.length === 0) {
      return infoNotification(t('class.portfolio.message3'));
    } else {
      execute4(multiApprovalProfolio(selectsKeys));
    }
  }

  const columns: TableColumnsType<IPortfolioItem> = [
    {
      title: t('common.label.1'),
      dataIndex: 'eduStepCode',
      key: 'eduStepCode',
      align: 'center',
      render: value => getCommonCodeLabel('PR0006', value, i18n.language),
    },
    {
      title: t('class.portfolio.column1'),
      dataIndex: 'learningTypeCode',
      align: 'center',
      sorter: (a, b) => a.learningTypeCode?.localeCompare(b.learningTypeCode),
      render: value => getCommonCodeLabel('GR0001', value, 'ko'),
    },
    {
      title: t('class.portfolio.column2'),
      dataIndex: 'subjectName',
      align: 'center',
    },
    {
      title: t('class.portfolio.1'),
      dataIndex: 'curriculumName',
      align: 'center',
      sorter: (a, b) => a.curriculumName?.localeCompare(b.curriculumName),
      render: value => value ? value : '-',
    },
    {
      title: t('class.portfolio.column3'),
      dataIndex: 'activityName',
      align: 'center',
      sorter: (a, b) => a.activityName?.localeCompare(b.activityName),
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      align: 'center',
      sorter: (a, b) => a.className?.localeCompare(b.className),
    },
    {
      title: t('class.portfolio.column4'),
      dataIndex: 'studentKorName',
      align: 'center',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
    },
    {
      title: t('class.portfolio.column12'),
      dataIndex: 'studentEngName',
      key: 'studentEngName',
      align: 'center',
      sorter: (a, b) => a.studentEngName?.localeCompare(b.studentEngName),
    },
    {
      title: t('class.portfolio.column5'),
      dataIndex: 'studentRegDate',
      align: 'center',
      sorter: (a, b) => a.studentRegDate?.localeCompare(b.studentRegDate),
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t("class.portfolio.column6"),
      dataIndex: 'approvalStatus',
      align: 'center',
      sorter: (a, b) => a.approvalStatus?.localeCompare(b.approvalStatus),
      render: (value) => getCommonCodeLabel('GR0004', value, 'ko'),
    },
    {
      title: t('class.portfolio.column7'),
      dataIndex: 'approvalDate',
      align: 'center',
      sorter: (a, b) => a.approvalDate?.localeCompare(b.approvalDate),
      render: value => value ? moment(value).format('YYYY.MM.DD HH:mm') : '-',
    },
    {
      title: t('class.portfolio.column8'),
      dataIndex: 'approvalMemberKorName',
      align: 'center',
      sorter: (a, b) => a.approvalMemberKorName?.localeCompare(b.approvalMemberKorName),
      render: value => value || '-',
    },
  ];

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('class.class.placeholder4')}</label>
            <div>
              <SelectSemester
                semesterCode={semesterCode}
                setSemesterCode={setSemesterCode}
              />
            </div>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label>
            <div>
              <SelectEduStep
                eduStepCode={eduStepCode}
                setEduStepCode={setEduStepCode}
                width={150}
                includeAll
              />
            </div>
          </li>
          <li>
            <label>{t('class.portfolio.column1')}</label>
            <div>
              <Select
                placeholder={t('class.portfolio.column1')}
                value={learningTypeCode}
                onChange={value => setLearningTypeCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                <Select.Option value="DT">POND</Select.Option>
                <Select.Option value="RV">e-Learning</Select.Option>
              </Select>
            </div>
          </li>
          <li>
            <label>{t('class.tutor.column6')}</label>
            <div>
            <Select
              placeholder={t('class.tutor.column6')}
              value={subjectCode}
              onChange={value => setSubjectCode(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {res5?.data?.map((item, index) => (
                <Select.Option
                  key={index}
                  value={item.subjectCode}
                >
                  {item.subjectName}
                </Select.Option>
              ))}
            </Select>
          </div>
          </li>
          <li>
            <label>{t('common.menu.class')}</label>
            <div>
              <Select
                placeholder={t('common.menu.class')}
                value={classCode}
                onChange={value => setClassCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {res2?.data?.map((item, index) => (
                  <Select.Option
                    key={index}
                    value={item.classCode}
                  >
                    {item.className}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('class.portfolio.column6')}</label>
            <div>
              <Select
                placeholder={t('class.portfolio.column6')}
                value={approvalStatus}
                onChange={value => setApprovalStatus(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {GR0004?.map((item: any) => {
                  return (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <input
              type="text"
              className="form-control"
              placeholder={t('class.eLearnAssess.placeholder1')}
              value={memberName}
              onChange={e => setMemberName(e.target.value)}
              onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	
    <div className="content scrollbar">
      {(isLoading || isLoading2 || isLoading3 || isLoading5) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      <ErrorResponse error={error5} />
      <SimpleHeader
        parentMenu={t('common.menu.class')}
        currentMenu={t('class.portfolio.title1')}
        onlyTitle={true}
      />

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onMultiApprovalCheck}
            text={t('class.portfolio.button1')}
          />
        </div>
      </div>
      <div className='comm-table__area'>
        <Table
          columns={columns}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          dataSource={res?.data}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
          onRow={(record) => {
            return {
              onClick: (e) => {
                const eleClsName = (e.target as HTMLInputElement).className;
                if(eleClsName.indexOf("ant-table-selection-column") === -1 && eleClsName.indexOf("ant-checkbox") === -1) {
                  setItem(record);
                  setShowPortfolioVisible(true);
                }
              },
            };
          }}
          rowClassName="pointer"    
          rowKey="resultCode"
        />
      </div>
      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            count={res?.total || 0}
            page={page}
            param={param}
            path="/class/portfolio"
          />
        </div>
      </div>

      {showPortfolioVisible &&
        <ShowPortfolioModal
          visible={showPortfolioVisible}
          setVisible={setShowPortfolioVisible}
          item={item}
          doGetPortfolios={doGetPortfolios}
        />      
      }

      {confirmApprovalVisible && (
        <ConfirmModal
          visible={confirmApprovalVisible}
          setVisible={setConfirmApprovalVisible}
          onConfirm={onMultiApproval}
          titleText={t('class.portfolio.title3')}
          messageText={t('class.portfolio.message2')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      )}
    </div>
    </>
  );
}

export default ClassPortfolio;
